import { LanguageSelector } from '@/components/LanguageSelector.tsx'
import { trackAuthenticatedOnce } from '@/trackAuthenticatedOnce'
import { logger } from '@jobilla/instrumentation'
import { useCandidacyShow } from '@jobilla/nexus-sdk/lib/hooks/useCandidacy'
import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Centered } from '../components/Centered'
import { DisplayDate } from '../components/DisplayDate'

export const Route = createFileRoute('/organisations/$organisationId/$candidacyId/')({
	head: () => ({
		meta: [{ title: 'Jobilla' }],
	}),
	component: SubmissionScreen,
})

function SubmissionScreen() {
	const { organisationId, candidacyId } = Route.useParams()
	const { t } = useTranslation('common')
	const candidacyQuery = useCandidacyShow(organisationId, candidacyId, { retry: 0 })

	useEffect(() => {
		if (!candidacyQuery.isSuccess) {
			return
		}
		trackAuthenticatedOnce(candidacyId)
		logger.track('Candidate Center: Viewed submission', {
			data: { submission_count: candidacyQuery.data.submissionCount, candidate_id: candidacyId },
		})
		// Fire once per successful load — not again when the count changes on refetch.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [candidacyQuery.isSuccess])

	if (candidacyQuery.isPending) {
		return (
			<Centered>
				<p className="text-sm text-grey-500">{t('login.loading')}</p>
			</Centered>
		)
	}

	if (candidacyQuery.isError) {
		const status = candidacyQuery.error.response?.status

		if (status === 401) {
			return <Navigate to="/" replace={true} />
		}

		return (
			<Centered>
				<p className="text-lg font-medium text-grey-800">
					{status === 404 ? t('submission.not_found.title') : t('login.error.title')}
				</p>
				<p className="max-w-sm text-sm text-grey-500">
					{status === 404 ? t('submission.not_found.body') : t('login.error.body')}
				</p>
				<Link
					to="/organisations/$organisationId"
					params={{ organisationId }}
					className="mt-4 text-sm font-medium text-brand-600 hover:text-brand-700"
				>
					{t('submission.back')}
				</Link>
			</Centered>
		)
	}

	const candidacy = candidacyQuery.data
	const { cv } = candidacy.profile
	const profileSummary = [
		candidacy.profile.name,
		cv !== null
			? t('profile.link.cv_added')
			: candidacy.collectsCv
			? t('profile.link.cv_missing')
			: null,
	]
		.filter(part => part !== null)
		.join(' · ')

	return (
		<div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-6 py-12">
			<header className="flex items-center justify-between gap-4">
				<Link
					to="/organisations/$organisationId"
					params={{ organisationId }}
					className="text-sm font-medium text-grey-700 hover:text-grey-900"
				>
					‹ {t('submission.back_short')}
				</Link>
				<LanguageSelector />
			</header>

			<div className="mt-8 flex flex-col items-center text-center">
				{candidacy.logoUrl === null
					? <p className="text-sm font-medium text-grey-800">{candidacy.organisation.name}</p>
					: (
						<img
							src={candidacy.logoUrl}
							alt={candidacy.organisation.name}
							className="h-24 w-auto object-contain"
						/>
					)}
			</div>

			<Link
				to="/organisations/$organisationId/$candidacyId/profile"
				params={{ organisationId, candidacyId }}
				className="mt-8 flex items-center gap-3 rounded-lg border border-grey-200 px-4 py-3 hover:bg-grey-50"
			>
				<span
					aria-hidden={true}
					className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600"
				>
					<svg
						viewBox="0 0 20 20"
						fill="none"
						stroke="currentColor"
						strokeWidth={1.6}
						strokeLinecap="round"
						strokeLinejoin="round"
						className="size-5"
					>
						<circle cx="10" cy="7" r="3.25" />
						<path d="M3.75 16.5c.9-2.9 3.3-4.5 6.25-4.5s5.35 1.6 6.25 4.5" />
					</svg>
				</span>
				<span className="min-w-0 flex-1">
					<span className="block text-md font-semibold text-grey-900">{t('profile.link.title')}</span>
					<span className="mt-0.5 block truncate text-sm text-grey-500">
						{profileSummary}
						{cv === null
							? null
							: (
								<>
									{' '}
									<DisplayDate date={new Date(cv.uploadedAt)} />
								</>
							)}
					</span>
				</span>
				<span aria-hidden={true} className="text-xl text-grey-400">›</span>
			</Link>

			<section className="mt-10">
				<h2 className="text-lg font-semibold text-grey-900">{t('submission.summary.title')}</h2>
				<p className="mt-1 text-sm text-grey-500">
					{t('submission.summary.submitted_on')} <DisplayDate date={new Date(candidacy.submittedAt)} />.
				</p>

				{candidacy.submissionCount > 1
					? (
						<p className="mt-4 rounded-lg border border-grey-200 bg-grey-50 px-6 py-4 text-sm text-grey-700">
							{t('submission.summary.submitted_more_than_once')}
						</p>
					)
					: null}

				<dl className="mt-4 flex flex-col gap-6">
					{candidacy.answers.map((answer, answerIndex) => (
						<div key={answerIndex}>
							<dt className="text-md mb-2 font-medium leading-tight text-grey-900">{answer.question}</dt>
							{answer.values.map((value, valueIndex) => (
								<dd key={valueIndex} className="text-md pb-1 border-b border-gray-300 text-grey-900">
									{value}
								</dd>
							))}
						</div>
					))}
				</dl>

				{candidacy.thankYouTitle === null && candidacy.thankYouHtml === null
					? null
					: (
						<section className="mt-8">
							<h2 className="text-lg font-semibold text-grey-900">{t('submission.result.heading')}</h2>
							<div className="mt-2 rounded-lg border border-grey-200 bg-grey-50 px-6 py-6 text-center">
								{candidacy.thankYouTitle === null
									? null
									: <h3 className="text-lg font-semibold text-grey-900">{candidacy.thankYouTitle}
									</h3>}
								{candidacy.thankYouHtml === null
									? null
									: (
										<div
											className="mt-2 text-sm text-grey-700"
											// eslint-disable-next-line react/no-danger -- server-sanitised by RichText::toHtml
											dangerouslySetInnerHTML={{ __html: candidacy.thankYouHtml }}
										/>
									)}
							</div>
						</section>
					)}
			</section>
		</div>
	)
}
